import React, { useEffect, useState, useRef } from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Spinner,
} from "@deere/ux.uxframe-react";
import { useDispatch, useSelector } from "react-redux";
import MachineList from "../machine-search/MachineList";
import {
  dealerMachineListError,
  getMachinesForDealer,
  machineListLoading,
  machinesForDealer,
  resetMachineState,
  setDealerSearchData,
} from "../machine-search/machineSearchSlice";
import {useOktaAuth} from "@okta/okta-react";
import { relatedAccounts, relatedAccountsSearchLoading } from "./DealerSlice";
import RegionalDetails from "./RegionalDetails";
import { languageResponseData } from "../Translation/translationSerachSlice";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { clearRegionalFilters } from "./regionalDetailsSlice";
import {
  DateRangePicker,
  InputAdornment,
  Tab,
  Tabs,
  ThemeProvider,
} from "@deere/fuel-react";
import { CalendarMonth } from "@mui/icons-material";

const DealerSearch = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [dealerIds, setDealerIds] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [tabValue, setTabValue] = useState(location.state?.activeTab ?? 0);
  const {authState} = useOktaAuth();

  // Clamp tabValue to 0 if the second tab is not available for this user type
  useEffect(() => {
    const isDealerEmployee = authState?.idToken?.claims?.userType === "Dealer Employee";
    if (isDealerEmployee && tabValue !== 0) {
      setTabValue(0);
    }
  }, [authState, tabValue]);
  const machineSearchLoading = useSelector(machineListLoading);
  const machines = useSelector(machinesForDealer);
  const relatedAccountList = useSelector(relatedAccounts);
  const relatedAccountsLoading = useSelector(relatedAccountsSearchLoading);
 
  const dealerMachineSearchError = useSelector(dealerMachineListError);

  const translationsResponse = useSelector(languageResponseData);
  const translate = (key, defaultText) => {
    return translationsResponse[key] || defaultText;
  };

  let relatedAccountOptions = [];
  let dealerSearchObj;
  if (relatedAccountList && relatedAccountList.length) {
    relatedAccountOptions = relatedAccountList.map((relatedAccount) => {
      const optionValue = `${relatedAccount.accountId} - ${
        relatedAccount.physicalAddressCity
          ? relatedAccount.physicalAddressCity + ", "
          : ""
      }${
        relatedAccount.physicalAddressState
          ? relatedAccount.physicalAddressState + " "
          : ""
      }${
        relatedAccount.physicalAddressCountry
          ? relatedAccount.physicalAddressCountry
          : ""
      }`;

      return (
        <option
          key={relatedAccount.accountId}
          defaultselected={dealerIds
            .includes(relatedAccount.accountId)
            .toString()}
          onClick={() => handleItemClick(relatedAccount.accountId)}
          value={dealerIds.includes(relatedAccount.accountId)}
        >
          {optionValue}
        </option>
      );
    });
  }

  const handleItemClick = (item) => {
    setDealerIds((prevDealerIds) => {
      return prevDealerIds.includes(item)
        ? prevDealerIds.filter(
            (selectedDealerIds) => selectedDealerIds !== item
          )
        : [...prevDealerIds, item];
    });
  };

  const onSearchMachinesForDealerSubmit = (event) => {
    event.preventDefault();
    const dealerQueryObj = {
      dealerIds: dealerIds,
      model: model,
      serial: serialNumber,
      initialDeliveryDateRange: deliveryDate
        ? deliveryDate[0].toISOString()
        : null,
      finalDeliveryDateRange: deliveryDate
        ? deliveryDate[1].toISOString()
        : null,
    };

    dealerSearchObj = {
      dealerQueryObj: dealerQueryObj,
      pageSize: 200,
      pageNumber: 1,
    };

    /*Set the dealer search data in redux state to preserve the search option.*/
    dispatch(setDealerSearchData(dealerSearchObj));
    dispatch(getMachinesForDealer(dealerSearchObj));

    setAlertMessage(
      "The export of all records may take some time. You can continue using Warranty Reports while the download is in progress."
    );
  };

  const onCloseAlert = () => {
    setAlertMessage("");
  };

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (!location.pathname.startsWith("/machine-details")) {
        dispatch(resetMachineState());
      }
      if (location.pathname === "/machine-search/dealer") {
        setModel("");
        setDeliveryDate([]);
        setSerialNumber("");
        setDealerIds([]);
      }
    });

    return () => {
      unlisten();
    };
  }, [dispatch, history]);


  return (
    <>
      {relatedAccountsLoading ? (
        <div className="d-flex w-100 justify-content-center align-items-center">
          <Spinner
            accessibilityLabel="Loading"
            className="uxf-spinner-border-lg"
            animation="border"
          />
        </div>
      ) : (
        <>
          <ThemeProvider>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => {
                if (newValue !== tabValue) {
                  dispatch(clearRegionalFilters());
                }
                setTabValue(newValue);
              }}
            >
              <Tab label="Dealer Account" />
              {authState?.idToken?.claims?.userType !== "Dealer Employee" && (
                <Tab label="Machine Population" />
              )}
            </Tabs>
          </ThemeProvider>

          {tabValue === 0 && (
            <>
          {alertMessage &&
          alertMessage !== "" &&
          !serialNumber &&
          !deliveryDate &&
          !model ? (
            <Alert
              dismissible
              onClose={onCloseAlert}
              accessibilityLabel="Info:"
              variant="info"
              aria-atomic="true"
            >
              <span className="uxf-alert-title info-content">
                {alertMessage}
              </span>
            </Alert>
          ) : null}
          <Card>
            <Card.Body>
              <Form onSubmit={onSearchMachinesForDealerSubmit}>
                <Form.Row>
                  <Form.Group as={Col} controlId="formGridDealer">
                    <Form.Label className="uxf-label">
                      {translate("dealer", " Dealer")}
                      <span
                        aria-label="field required"
                        className="uxf-is-required"
                      >
                        *
                      </span>
                    </Form.Label>
                    {relatedAccountList && relatedAccountList.length ? (
                      <>
                        <Form.Control required as="select" multiple>
                          <option key="-1">
                            Select single or multiple dealer ids....
                          </option>
                          {relatedAccountOptions}
                        </Form.Control>

                        <div style={{ marginTop: "20px" }}>
                          <strong>Selected Dealer Ids:</strong>
                          {dealerIds.join(", ")}
                        </div>
                      </>
                    ) : (
                      <Form.Control
                        required
                        placeholder={translate(
                          "enter_dealer_account",
                          "Enter Dealer Account Number"
                        )}
                        value={dealerIds}
                        onChange={(e) =>
                          setDealerIds([e.target.value.replace(/\s/g, "")])
                        }
                        maxLength="6"
                      />
                    )}
                  </Form.Group>
                  <Form.Group as={Col} controlId="formGridModel">
                    <Form.Label className="uxf-label">
                      {translate("model", "Model")}
                    </Form.Label>
                    <Form.Control
                      value={model}
                      placeholder={translate("enter_model", "Enter Model")}
                      onChange={(e) => setModel(e.target.value)}
                      maxLength="10"
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group
                    as={Col}
                    style={{
                      marginTop: 2,
                    }}
                  >
                    <Form.Label className="uxf-label">
                      {translate("dealivery_date", "Delivery Date Between")}
                    </Form.Label>

                    <ThemeProvider>
                    <DateRangePicker
                      EndInputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonth />
                          </InputAdornment>
                        ),
                      }}
                      StartInputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonth />
                          </InputAdornment>
                        ),
                      }}
                      endDatePlaceholder="Select End Date"
                      startDatePlaceholder="Select Start Date"
                      onChange={(date) => setDeliveryDate(date)}
                      value={deliveryDate}
                    /></ThemeProvider>

                  </Form.Group>
                  <Form.Group as={Col} controlId="formGridSerialNumber">
                    <Form.Label className="uxf-label">
                      {translate("serial_number", "Serial Number")}
                    </Form.Label>
                    <Form.Control
                      value={serialNumber}
                      placeholder={translate(
                        "enter_serial_no",
                        "Enter Serial Number"
                      )}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      maxLength="6"
                    />
                  </Form.Group>
                </Form.Row>
                <p>
                  {" "}
                  {translate(
                    "message15",
                    " * Retrieves all machines dealer is responsible for with this model and/or serial number."
                  )}
                </p>
                <Button variant="primary" type="submit">
                  {translate("search", "Search")}
                </Button>
              </Form>

              {dealerMachineSearchError ? (
                <span
                  style={{
                    color: "red",
                    marginTop: 0.5,
                    fontSize: 20,
                    marginLeft: 300,
                  }}
                >
                  {translate(
                    "message16",
                    "No machines found for this criteria."
                  )}
                </span>
              ) : (
                <></>
              )}
            </Card.Body>
          </Card>
          {machineSearchLoading ? (
            <div className="d-flex w-100 justify-content-center align-items-center">
              <Spinner
                accessibilityLabel="Loading"
                className="uxf-spinner-border-lg"
                animation="border"
              />
            </div>
          ) : machines && machines.length > 0 ? (
            <Card>
              <Card.Header>
                <strong>{translate("machine_list", "Machine List")}</strong>
              </Card.Header>
              <Card.Body>
                <MachineList machineSearchType="dealer" />
              </Card.Body>
            </Card>
          ) : (
            <></>
          )}
            </>
          )}

          {tabValue === 1 && <RegionalDetails />}
        </>
      )}
    </>
  );
};

export default DealerSearch;
